import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';

import type { HydrateAction } from 'redux-store/index';
import filterProductsByPage from 'redux-store/slices/products/utils/filterProductsByPage';
import filterSmallerProducts from 'redux-store/slices/products/utils/filterSmallerProducts';
import getDiscountedPrice from 'redux-store/slices/products/utils/getDiscountedPrice';
import getYearPrice from 'redux-store/slices/products/utils/getYearPrice';
import type { PurchaseType } from 'redux-store/slices/purchases/types';
import couponCheckThunk from 'redux-store/thunks/products/couponCheckThunk';
import getProductsThunks from 'redux-store/thunks/products/getProductsThunks';

import type { ProductsState } from './types';

const initialState = {
  isFetched: false,
  products: [],
  productPage: 'main',
  couponIsValid: null,
} as ProductsState;

const ProductsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProductPage(state, action) {
      state.productPage = action.payload;
    },
    setPurchasedProducts(state, action: PayloadAction<PurchaseType[]>) {
      if (state.isFetched) {
        let products = state.products;

        products = products.map(product => {
          const foundPurchase = action.payload.find(purchase => {
            return (
              purchase.payment_plan.welltory_payment_plan_id === product.id
            );
          });

          if (foundPurchase && !foundPurchase.is_closed) {
            product.isCurrent = true;
            product.nextPayment = foundPurchase.next_pay;
            product.isCanceled =
              foundPurchase.status === 'failed' ||
              foundPurchase.status === 'cancelled';
          }

          return product;
        });

        state.products = filterSmallerProducts(products);
      }
    },
    setYearPrice(state) {
      if (state.isFetched) {
        const productIndex = state.products.findIndex(
          product => product.interval === 'year',
        );

        if (productIndex !== -1) {
          const {
            price: yearPrice,
            discountedPrice: yearDiscountedPrice,
            totalYearPrice,
          } = getYearPrice(state.products);

          state.products[productIndex].price = yearPrice;
          state.products[productIndex].discountedPrice = yearDiscountedPrice;
          state.products[productIndex].totalPrice = totalYearPrice;
        }
      }
    },
    setGiftProduct(state) {
      if (state.isFetched) {
        const giftProduct = state.products.find(
          product => product.slug === 'welltorygift',
        );

        if (giftProduct) {
          giftProduct.interval = 'year-gift';
          state.products = [giftProduct];
        }
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(getProductsThunks.fulfilled, (state, action) => {
      if (action.payload.success === true) {
        let products = [];

        products = filterProductsByPage(
          action.payload.products,
          state.productPage,
        );

        if (products.length === 0) {
          const defaultPage = 'main';
          products = filterProductsByPage(action.payload.products, defaultPage);
        }

        // products = products.filter(product => product.interval !== 'lifetime');
        // products = products.filter(product => product.interval !== '2-year');
        // products = products.filter(product => product.interval !== 'year');

        products.sort((a, b) => {
          return a.order - b.order;
        });

        products = products
          .filter(({ isCurrent, extra }) => {
            if (extra.discount) {
              return !isCurrent;
            }

            return true;
          })
          .map(product => {
            const { extra } = product;
            const extraFields: {
              discountedPrice?: string | number;
              price?: number;
              discount?: number;
            } = {};

            if (extra.discount && extra.priceWithoutDiscount) {
              extraFields.discount = extra.discount;
              extraFields.discountedPrice = product.price;
              extraFields.price = extra.priceWithoutDiscount;
            }

            return {
              ...product,
              ...extraFields,
              totalPrice: product.price,
            };
          });

        state.isFetched = true;
        state.products = products;
      }
    });

    builder.addCase(couponCheckThunk.fulfilled, (state, action) => {
      if (action.payload.success === true && state.isFetched === true) {
        state.couponIsValid = true;
        state.products = state.products.map(item => {
          delete item.discount;
          delete item.discountedPrice;

          return item;
        });

        action.payload.coupon_is_valid_for_products.map(coupon => {
          const productIndex = state.products.findIndex(
            p => p.id === coupon.product_id,
          );

          if (productIndex !== -1) {
            const product = state.products[productIndex];
            const { discount } = coupon;

            product.discount = discount;

            const discountedPrice = getDiscountedPrice(product.price, discount);

            product.discountedPrice = discountedPrice;
            product.totalPrice = discountedPrice;
          }
        });
      } else {
        state.couponIsValid = false;
      }
    });

    builder.addCase(HYDRATE, (state, action: HydrateAction<ProductsState>) => {
      return action.payload.products;
    });
  },
});

export const {
  setProductPage,
  setYearPrice,
  setPurchasedProducts,
  setGiftProduct,
} = ProductsSlice.actions;

export default ProductsSlice.reducer;
